// Side-effect import: <std-dsh-turn-row> must be registered before this
// hydrates one, and the class is otherwise referenced only as a type.
import "./row";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import type { DshTurnRow } from "./row";

/** `attachment`, in the dsh register — something the turn produced or cited,
 * as a row rather than as a chip.
 *
 * The native skin draws a chip because chips CLUSTER: several attachments at
 * the end of a turn wrap into a grid and read as a set. dsh has no cluster to
 * draw — every event in this skin is a 24px line in one column — so an
 * attachment is a line like the rest, and what tells it apart from a tool
 * call is its label word and its glyph, not its shape.
 *
 * It is the one row in the skin that does not disclose. `body: null`, which
 * the row reads as "nothing to open": no chevron, no click, no focus stop, no
 * hover light. What it does instead is ACTIVATE, when the caller gave it an
 * `onOpen`. Without one it is a plain line, which is the right thing in a
 * read-only transcript — a row that lights up under the cursor is promising
 * something it cannot do.
 *
 * Activation is bound to this component's own wrapper rather than to anything
 * inside the row, because the row is not an interactive primitive and should
 * not become one for a single caller. Events from its shadow tree retarget to
 * its host and bubble here, so one binding covers the whole line. It goes
 * through the house gesture manager on pointerdown/up rather than through a
 * click handler: the manager owns the app's gesture lock, so a row bound with
 * onclick fires under a drag the rest of the app has already claimed.
 *
 * The KIND becomes the label word — File, Image, Link, Artifact — and the
 * glyph stays the same for all four. That is the opposite of the native
 * skin's answer and follows from the layout rather than from taste: a chip
 * cluster is sorted by eye, so there the kind has to be in the mark; a single
 * column is read, so the kind is a word in the label position and the glyph
 * only has to say "attachment". */
@component({ name: "std-dsh-turn-attachment" })
export class DshTurnAttachment extends LonaWebComponent {
  private $root: HTMLElement = this.$("root");
  private $row: DshTurnRow = this.hydrate("row");
  /** Reset on EVERY bind. A pooled row arriving from an openable attachment
   * must not keep firing that attachment's callback. */
  private open: Option<() => void> = null;
  /** The pointer binding is one-time: the manager writes handlers onto the
   * element, and the element outlives every bind. */
  private bound: boolean = false;

  static makeWith(props: DshTurnAttachment.Props): DshTurnAttachment {
    return (DshTurnAttachment.make() as DshTurnAttachment).bind(props);
  }

  bind(props: DshTurnAttachment.Props): this {
    const kind = props.kind ?? null;
    this.setAttribute("kind", kind ?? "none");
    this.$row.bind({
      kind: "attachment",
      label: DshTurnAttachment.label(kind),
      subject: props.label,
      suffix: props.meta ?? null,
      body: null,
      // The pointer cursor over an inert-but-activating row — the handler
      // itself is on this host, below.
      interactive: props.onOpen != null,
    });
    this.open = props.onOpen ?? null;
    this.toggleAttribute("interactive", this.open != null);
    if (!this.bound) {
      this.bound = true;
      GESTURE_MANAGER.addPointerEvent(this.$root, {
        onClick: () => this.open?.(),
      });
    }
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      #root {
        width: 100%;
        min-width: 0;
        border-radius: 4px;
      }
      :host([interactive]) #root {
        cursor: pointer;
      }
      /* The wash bleeds 6px past the row on each side, the same bleed the
         disclosure rows' hover light uses, so a mixed column highlights to
         one width. It is drawn here rather than by the row because the row is
         inert — it has no body, so it lights nothing of its own. */
      :host([interactive]) #root:hover {
        background: var(--bg-hover, rgba(127, 127, 127, 0.1));
        box-shadow: 0 0 0 var(--dsh-row-bleed-x, 6px)
          var(--bg-hover, rgba(127, 127, 127, 0.1));
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-dsh-turn-row id="row"></std-dsh-turn-row>
    </div>
  `;
}

export namespace DshTurnAttachment {
  export type Kind = "file" | "image" | "link" | "artifact";

  /** The label word. A known kind names itself; an absent one falls back to
   * the category, because a row that labelled itself "File" on a producer
   * that never said so would be inventing a fact. */
  export function label(kind: Option<Kind>): string {
    if (kind == null) return "Attachment";
    return kind.charAt(0).toUpperCase() + kind.slice(1);
  }

  export type Props = {
    label: string;
    kind?: Kind;
    /** Size, media type, line count — whatever identifies the thing. */
    meta?: string;
    onOpen?: () => void;
  };
}
